/**
 * Distance calculation utilities using Nominatim geocoding and Turf.js
 */

import distance from '@turf/distance';
import { point } from '@turf/helpers';

interface Coordinates {
  lat: number;
  lng: number;
}

interface DistanceResult {
  straightLineKm: number;
  estimatedRoadKm: number;
  estimatedDurationMinutes: number;
  cost: number;
}

/**
 * Geocode an address using Nominatim (OpenStreetMap)
 * Free service, rate limit: 1 request per second
 * @param address - Full address to geocode
 * @returns Coordinates (lat, lng)
 */
export async function geocodeAddress(address: string): Promise<Coordinates> {
  const searchQuery = encodeURIComponent(address + ', Switzerland');

  const url = `https://nominatim.openstreetmap.org/search?` +
    `format=json&` +
    `q=${searchQuery}&` +
    `limit=1&` +
    `addressdetails=1`;

  const response = await fetch(url, {
    headers: {
      'User-Agent': 'WebKreditCH/1.0 (bank-credit-application)',
    },
  });

  if (!response.ok) {
    throw new Error(`Geocoding failed: ${response.statusText}`);
  }

  const data = await response.json();

  if (data && data.length > 0) {
    return {
      lat: parseFloat(data[0].lat),
      lng: parseFloat(data[0].lon),
    };
  }

  throw new Error(`Address not found: ${address}`);
}

/**
 * Calculate distance between two coordinates using Turf.js
 * More accurate and battle-tested than manual Haversine implementation
 * @param from - Starting coordinates
 * @param to - Ending coordinates
 * @returns Distance in kilometers
 */
export function calculateDistance(from: Coordinates, to: Coordinates): number {
  const fromPoint = point([from.lng, from.lat]);
  const toPoint = point([to.lng, to.lat]);

  return distance(fromPoint, toPoint, { units: 'kilometers' });
}

/**
 * Calculate commute distance and costs between home and work
 * @param homeAddress - Full home address
 * @param workAddress - Full work address
 * @param costPerKm - Cost per kilometer (default: CHF 0.70 - Swiss standard)
 * @returns Distance and cost breakdown
 */
export async function calculateCommuteDistance(
  homeAddress: string,
  workAddress: string,
  costPerKm: number = 0.70,
): Promise<DistanceResult> {
  // Step 1: Geocode home address
  console.log('[Distance] Geocoding home address:', homeAddress);
  const homeCoords = await geocodeAddress(homeAddress);
  console.log('[Distance] Home coordinates:', homeCoords);

  // Step 2: Wait 1.1 seconds (Nominatim rate limit: 1 req/sec)
  await new Promise((resolve) => setTimeout(resolve, 1100));

  // Step 3: Geocode work address
  console.log('[Distance] Geocoding work address:', workAddress);
  const workCoords = await geocodeAddress(workAddress);
  console.log('[Distance] Work coordinates:', workCoords);

  // Step 4: Calculate straight-line distance using Turf.js
  const straightLineKm = calculateDistance(homeCoords, workCoords);

  // Step 5: Estimate road distance
  // Factor of 1.35 accounts for roads not being straight lines
  // In Switzerland with mountains/lakes, roads can be 30-40% longer
  const roadFactor = 1.35;
  const estimatedRoadKm = straightLineKm * roadFactor;

  // Step 6: Estimate duration (assume 50 km/h average including traffic)
  const averageSpeedKmh = 50;
  const estimatedDurationMinutes = Math.ceil(
    (estimatedRoadKm / averageSpeedKmh) * 60,
  );

  // Step 7: Calculate costs
  const roundTripKm = estimatedRoadKm * 2;
  const cost = roundTripKm * costPerKm;

  const result: DistanceResult = {
    straightLineKm: Math.round(straightLineKm * 10) / 10,
    estimatedRoadKm: Math.round(estimatedRoadKm * 10) / 10,
    estimatedDurationMinutes,
    cost: Math.round(cost * 100) / 100,
  };

  console.log('[Distance] Calculation result:', result);

  return result;
}

/**
 * Build full address string from components
 */
export function buildAddressString(
  street?: string,
  houseNumber?: string,
  postal?: string,
  city?: string,
): string {
  const parts: string[] = [];

  if (street && houseNumber) {
    parts.push(`${street} ${houseNumber}`);
  } else if (street) {
    parts.push(street);
  }

  if (postal && city) {
    parts.push(`${postal} ${city}`);
  } else if (city) {
    parts.push(city);
  }

  return parts.join(', ');
}
